import React from 'react';
import PropTypes from 'prop-types';

import { InsOutsTeamGrid } from './InsOutsTeamGrid';

/**
 * Renders the wrapper and title for a single team.
 *
 * @param {object} props The props.
 * @param {object} props.data The Ins/Outs player data.
 * @param {string} props.title The value printed to the section, i.e. 'IN' or 'OUT'.
 * @param {string} props.matchTitle The match title, e.g. 'Sydney Swans v GWS GIANTS'.
 * @param {string} [props.cssClass] The CSS class that is applied to the title, i.e. 'in' or 'out'.
 * @param {string} props.teamDisplay The team to display, i.e. 'home' or 'away'.
 * @param {string} [props.homeTeamId] The home team ID.
 * @param {string} [props.awayTeamId] The away team ID.
 * @param {boolean} [props.isAFLW] Whether the competition is AFLW.
 * @returns {React.JSX.Element} The Ins/Outs row for a single team.
 */
export const InsOutsRowSingle = ({
    data,
    title,
    matchTitle,
    cssClass,
    teamDisplay,
    homeTeamId,
    awayTeamId,
    isAFLW = false
}) => {
    const isHome = teamDisplay === 'home';
    const players = isHome ? data.home : data.away;
    const teamId = isHome ? homeTeamId : awayTeamId;

    // Construct the CSS class for the title
    const titleCssClass = cssClass
        ? `team-lineups-ins-and-outs__title team-lineups-ins-and-outs__title--${cssClass}`
        : 'team-lineups-ins-and-outs__title';

    return (
        <div className="team-lineups-condensed-embeddable__ins-and-outs">
            <div className="team-lineups-condensed-embeddable__ins-and-outs-single">
                <div className="team-lineups-condensed-embeddable__ins-and-outs-single-label">
                    <div className="team-lineups-ins-and-outs__title-wrapper">
                        <p className={titleCssClass}>{title}</p>
                    </div>
                </div>
                <div className="team-lineups-condensed-embeddable__ins-and-outs-single-value">
                    <InsOutsTeamGrid
                        players={players}
                        side={teamDisplay}
                        isReversed={isHome}
                        matchData={data}
                        matchTitle={matchTitle}
                        teamId={teamId}
                        isAFLW={isAFLW}
                    />
                </div>
            </div>
        </div>
    );
};

InsOutsRowSingle.propTypes = {
    data: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    matchTitle: PropTypes.string.isRequired,
    cssClass: PropTypes.string,
    teamDisplay: PropTypes.oneOf(['home', 'away']).isRequired,
    homeTeamId: PropTypes.string,
    awayTeamId: PropTypes.string,
    isAFLW: PropTypes.bool
};
